import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Calculator, BarChart3, FileText, Compass, MessageSquare } from 'lucide-react';

const PAGE_CTAS = {
  '/': { label: 'Get a Free Assessment', href: '/evaluate', icon: Compass, color: 'taxera-gradient' },
  '/value': { label: 'Get a Free Assessment', href: '/evaluate', icon: Compass, color: 'taxera-gradient' },
  '/products': { label: 'Build Your Business Case', href: '/evaluate/business-case', icon: Calculator, color: 'bg-taxera-blue' },
  '/platform': { label: 'See It In Action', action: 'demo', icon: BarChart3, color: 'taxera-gradient' },
  '/evaluate': { label: 'Build Your Business Case', href: '/evaluate/business-case', icon: Calculator, color: 'bg-taxera-teal' },
  '/evaluate/benchmarks': { label: 'Start Your Assessment', href: '/evaluate', icon: Compass, color: 'bg-taxera-blue' },
  '/sap-s4hana': { label: 'Take the Readiness Assessment', href: '/sap-s4hana/assessment', icon: FileText, color: 'bg-violet-600' },
  '/insights': { label: 'View Benchmark Reports', href: '/evaluate/benchmarks', icon: BarChart3, color: 'bg-taxera-teal' },
  '/blog': { label: 'Explore Compliance Tools', href: '/evaluate', icon: Compass, color: 'taxera-gradient' },
  '/news': { label: 'See How We Help', href: '/evaluate/business-case', icon: Calculator, color: 'bg-taxera-blue' },
  '/about': { label: 'Talk to Our Team', action: 'demo', icon: MessageSquare, color: 'taxera-gradient' },
};

const HIDDEN_PATHS = ['/admin', '/login', '/register', '/settings', '/dashboard', '/contact'];

const FloatingCTA = ({ onBookDemo }) => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400 && !dismissed) {
        setVisible(true);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [dismissed]);

  const isHidden = HIDDEN_PATHS.some(p => location.pathname.startsWith(p));
  if (isHidden || dismissed) return null;

  const cta = PAGE_CTAS[location.pathname] || PAGE_CTAS['/'];
  const Icon = cta.icon;

  const handleClick = () => {
    if (cta.action === 'demo') {
      onBookDemo?.();
    } else if (cta.href) {
      window.location.href = cta.href;
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 20 }}
          className="fixed bottom-6 right-6 z-50"
          data-testid="floating-cta">
          <button onClick={handleClick}
            className={`${cta.color} text-white px-5 py-3 rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-105 flex items-center gap-2 text-sm font-semibold`}
            data-testid="floating-cta-btn">
            <Icon className="w-4 h-4" />
            {cta.label}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingCTA;
